/**
 * Canonical movement resolution for the sidebar list (spec
 * `vehicle-realtime-data-consistency`, task 3.8 / design step 8).
 *
 * Kept in its own module so the reconciliation the sidebar renders is pure and
 * testable without mounting `ListCard` (and its map/component import graph).
 */
import { getVehicleMovementStatus, resolveTrackingStatus } from "../utils";

export const getListStatusTime = (cardType, liveMapData, lastUpdated, sourceTime) => {
  if (cardType === "Devices") {
    return liveMapData.statusTime === undefined ? lastUpdated : liveMapData.statusTime;
  }
  if (cardType === "Vehicles" && lastUpdated !== undefined) return lastUpdated;
  return liveMapData.sourceTime || liveMapData.serverTime || lastUpdated || sourceTime;
};

/**
 * Formats a raw canonical speed for display (design 3.3): metres per second are
 * converted with the existing `(speed * 3.6).toFixed(1)` rule, and zero stays a
 * valid speed. `null` is returned when there is nothing to show, so the caller
 * keeps applying its own fallback text.
 *
 * @param {number|null|undefined} speedMps Raw canonical speed in m/s.
 * @returns {string|null} e.g. `"0.7 km/h"`, or `null` when speed is unavailable.
 */
export const formatCanonicalSpeed = (speedMps) => {
  if (speedMps === null || speedMps === undefined || speedMps === "") return null;

  const numericSpeed = Number(speedMps);
  if (!Number.isFinite(numericSpeed)) return null;

  return `${(numericSpeed * 3.6).toFixed(1)} km/h`;
};

/**
 * Resolves one sidebar row's movement from the canonical telemetry store.
 *
 * The sidebar used to recompute speed and status from whatever raw response was
 * at hand (the latest-vehicles fields on the row plus the realtime `mapData`
 * map), which is exactly how it could disagree with the live summary (#119201)
 * and age a speed by a generic packet time (#119052). Here speed, freshness and
 * ignition come from the single reconciled `movement` object instead, and the
 * status icon and the duration label are derived from the SAME
 * `movement.observedAt`, so they cannot contradict each other (bugfix.md 2.4).
 *
 * When the store has not reconciled the plate (no `telemetry`, e.g. the
 * Devices/Trips/Events lists, or an unknown plate) the previous behavior is kept
 * unchanged, including the legacy `mapData` fallback supplied by the caller.
 *
 * @param {Object} args
 * @param {Object} [args.telemetry] Canonical getters from `useVehicleTelemetry`.
 * @param {string} [args.plateNumber] Plate of the row.
 * @param {Object} [args.fallback] `{ observedAt, rawSpeed, ignition, movementStatus }`.
 * @returns {{ observedAt: number|null, rawSpeed: number|null, ignition: *,
 *   movementStatus: string, isCanonical: boolean }}
 */
export const resolveListCardMovement = ({ telemetry, plateNumber, fallback = {} } = {}) => {
  const movement = telemetry?.getCurrentMovementByPlateNumber?.(plateNumber) ?? null;
  const entry = telemetry?.getCurrentTelemetryByPlateNumber?.(plateNumber) ?? null;
  const isCanonical = Boolean(movement || entry);

  if (!isCanonical) {
    const observedAt = fallback.observedAt ?? null;
    const rawSpeed = fallback.rawSpeed ?? null;

    return {
      observedAt,
      rawSpeed,
      ignition: fallback.ignition,
      movementStatus:
        fallback.movementStatus ||
        getVehicleMovementStatus(fallback.ignition, rawSpeed, observedAt),
      isCanonical: false,
    };
  }

  const ignition = entry?.ignition ? entry.ignition.value : fallback.ignition;
  const { observedAt, rawSpeed, movementStatus } = resolveTrackingStatus(
    movement,
    ignition,
    fallback.deviceDataAt,
  );

  return {
    observedAt,
    rawSpeed,
    ignition,
    movementStatus,
    isCanonical: true,
  };
};
